import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownGroup} from '../../rules/ruleEditor/dropdowns/multipleGroupsDropdownHelpers';
import {RuleConditionCard, RuleConditionCardProps} from '../../rules/ruleEditor/ruleConditionCard';
import {AndLabel, OrLabel} from '../../rules/ruleEditor/ruleEditorCommons';
import {RuleConditionDefinition} from '../../rules/ruleParameterModel';
import {ConditionAddBlock, ConditionsAddBlockTypesEnum} from './conditionAddBlock';

/*
 * Props.
 */

interface ConditionAndBlockProps {
  isAdvancedEditor: boolean;
  shouldDisplayAdvancedAndBlock: boolean;
  canUpdateConditions: boolean;

  conditions: ReadonlyArray<RuleConditionCardProps>;

  conditionGroups: ReadonlyArray<DropdownGroup<RuleConditionDefinition>>;
  onAddCondition: HandlerOf<RuleConditionDefinition>;
}

/*
 * Style.
 */

interface ConditionsAndBlockWrapperStyleProps {
  $isAdvanced: boolean;
  $shouldDisplayAddBlock: boolean;
}

const ConditionsAndBlockWrapperDiv = styled.div<ConditionsAndBlockWrapperStyleProps>`
  border-radius: 28px;
  background: ${(p) => p.theme.greys.white};
  padding-bottom: 2px;
  ${({$isAdvanced, theme}) =>
    $isAdvanced &&
    css`
      margin: 0 -12px;
      padding: 12px;
      background: ${theme.greys.shade10};
    `}
  ${({$shouldDisplayAddBlock}) =>
    $shouldDisplayAddBlock &&
    css`
      padding-bottom: 12px;
    `}
`;

/*
 * Component.
 */

export const ConditionsAndBlock: FC<ConditionAndBlockProps> = (props) => {
  const {
    isAdvancedEditor,
    shouldDisplayAdvancedAndBlock,
    canUpdateConditions,
    conditions,
    conditionGroups,
    onAddCondition,
  } = props;

  return (
    <>
      <ConditionsAndBlockWrapperDiv
        $isAdvanced={isAdvancedEditor}
        $shouldDisplayAddBlock={canUpdateConditions}
      >
        {conditions.map(
          (
            {
              condition,
              conditionDefinition,
              shouldHideError,
              onUpdateCondition,
              onRemoveCondition,
              onValidation,
            },
            i,
          ) => (
            <div key={condition.clientId}>
              <RuleConditionCard
                condition={condition}
                conditionDefinition={conditionDefinition}
                shouldHideError={shouldHideError}
                onUpdateCondition={onUpdateCondition}
                onRemoveCondition={canUpdateConditions ? onRemoveCondition : undefined}
                onValidation={onValidation}
              />
              {maybeRenderOrAndLabel(i, conditions)}
            </div>
          ),
        )}
        {canUpdateConditions && (
          <>
            <OrLabel />
            <ConditionAddBlock
              type={ConditionsAddBlockTypesEnum.OR}
              shouldShowAdvanced={isAdvancedEditor}
              conditionGroups={conditionGroups}
              onAddCondition={onAddCondition}
            />
          </>
        )}
      </ConditionsAndBlockWrapperDiv>
      {shouldDisplayAdvancedAndBlock && <AndLabel />}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderOrAndLabel(i: number, conditions: ReadonlyArray<RuleConditionCardProps>) {
  return i < conditions.length - 1 ? <OrLabel /> : null;
}
